/**
 * Per-sub-tab filter persistence using sessionStorage.
 *
 * The Scholarship detail page's Players and Depth chart sub-tabs each maintain
 * their own filter state (position groups + classes) so navigating between
 * tabs doesn't clear what the coach was just looking at. Storage is scoped per
 * scholarshipId so filters from one scholarship don't bleed into another.
 *
 * Mirrors the CRM filter-persistence pattern used elsewhere in /web.
 */

export const FILTER_PARAM_KEYS = ['positions', 'classes'] as const;

export type FilterScope = 'players' | 'depthChart';

const buildKey = (scope: FilterScope, scholarshipId: string): string =>
  `rp:scholarship:${scholarshipId}:filters:${scope}`;

/**
 * Writes the current filter-related URL params to sessionStorage. Only the
 * keys in FILTER_PARAM_KEYS are persisted — unrelated params on the URL
 * (modals, sort, etc.) are ignored.
 */
export const saveScholarshipFilters = (
  scope: FilterScope,
  scholarshipId: string,
  searchParams: URLSearchParams,
): void => {
  try {
    const filterOnly = new URLSearchParams();
    for (const k of FILTER_PARAM_KEYS) {
      const v = searchParams.get(k);
      if (v) filterOnly.set(k, v);
    }
    sessionStorage.setItem(buildKey(scope, scholarshipId), filterOnly.toString());
  } catch {
    // sessionStorage may be full or disabled (Safari private mode); silently no-op.
  }
};

/**
 * Reads previously-saved filter params for this scope + scholarship.
 * Returns null if nothing saved or storage was unreadable. Returns null if
 * the saved value is empty (no filters) so callers can `if (!saved) return`.
 */
export const loadScholarshipFilters = (
  scope: FilterScope,
  scholarshipId: string,
): URLSearchParams | null => {
  try {
    const raw = sessionStorage.getItem(buildKey(scope, scholarshipId));
    if (!raw) return null;
    const parsed = new URLSearchParams(raw);
    const hasAny = FILTER_PARAM_KEYS.some((k) => parsed.get(k));
    return hasAny ? parsed : null;
  } catch {
    return null;
  }
};

// Depth-chart "Totals vs Average" display mode, per-scholarship. Defaults to 'total'
// until the coach flips it; the choice then persists across reloads and sub-tab nav.
export type DepthChartDisplayMode = 'total' | 'average';

const buildDisplayModeKey = (scholarshipId: string): string =>
  `rp:scholarship:${scholarshipId}:depthChart:displayMode`;

export const saveDepthChartDisplayMode = (
  scholarshipId: string,
  mode: DepthChartDisplayMode,
): void => {
  try {
    sessionStorage.setItem(buildDisplayModeKey(scholarshipId), mode);
  } catch {
    // sessionStorage may be full or disabled (Safari private mode); silently no-op.
  }
};

export const loadDepthChartDisplayMode = (
  scholarshipId: string,
): DepthChartDisplayMode | null => {
  try {
    const raw = sessionStorage.getItem(buildDisplayModeKey(scholarshipId));
    return raw === 'total' || raw === 'average' ? raw : null;
  } catch {
    return null;
  }
};

// Depth-chart zoom, per-scholarship. Sits alongside the display mode above because it is the
// same kind of thing: a view preference the coach set on this board, restored when they come
// back to it. sessionStorage for the same reason as its neighbours -- one module, one backend.
//
// Deliberately NOT a URL param, unlike the filters. Zoom is not something a coach shares or
// deep-links, and pushing a param on every click of a repeat-pressed button would fill their
// back button with zoom levels.
const buildZoomKey = (scholarshipId: string): string =>
  `rp:scholarship:${scholarshipId}:depthChart:zoom`;

export const saveDepthChartZoom = (scholarshipId: string, zoom: number): void => {
  try {
    sessionStorage.setItem(buildZoomKey(scholarshipId), String(zoom));
  } catch {
    // sessionStorage may be full or disabled (Safari private mode); silently no-op.
  }
};

/**
 * The saved zoom, or null when there is none or it cannot be read. Returns null rather than a
 * default so the caller owns what "no preference" means, and a corrupt value (a non-number, or
 * one written by a build with different bounds) reads as no preference instead of throwing.
 */
export const loadDepthChartZoom = (scholarshipId: string): number | null => {
  try {
    const raw = sessionStorage.getItem(buildZoomKey(scholarshipId));
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

// Depth-chart layout (field or list), per coach rather than per board: it follows their screen,
// not the roster. Only an explicit choice is stored; with none, the layout follows the width.
const DEPTH_CHART_VIEW_KEY = 'rp:depthChart:view';

export type DepthChartView = 'field' | 'list';

export const saveDepthChartView = (view: DepthChartView): void => {
  try {
    sessionStorage.setItem(DEPTH_CHART_VIEW_KEY, view);
  } catch {
    // sessionStorage may be full or disabled (Safari private mode); silently no-op.
  }
};

export const loadDepthChartView = (): DepthChartView | null => {
  try {
    const raw = sessionStorage.getItem(DEPTH_CHART_VIEW_KEY);
    return raw === 'field' || raw === 'list' ? raw : null;
  } catch {
    return null;
  }
};
